import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/shadcn/HoverCard/HoverCard";
import type { MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import { Portal } from "@radix-ui/react-hover-card";
import { Link } from "@tanstack/react-router";
import { Link2 } from "lucide-react";
import { useState } from "react";

export const RelatedMatterIndicator = ({
  matters,
}: {
  matters: MatterSearchResult["relatedMatters"];
}) => {
  const [open, setOpen] = useState(false);
  if (!matters?.length) return null;

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`${matters.length} related ${matters.length === 1 ? "matter" : "matters"}`}
          aria-expanded={open}
          className="focus-visible:ring-ring inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs text-blue-600 hover:text-blue-800 focus-visible:ring-2 focus-visible:outline-none dark:text-blue-400 dark:hover:text-blue-300"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Link2 className="size-3.5" />
          <span>{matters.length}</span>
        </button>
      </HoverCardTrigger>
      <Portal>
        <HoverCardContent
          align="start"
          className="pointer-events-auto z-[110] w-80 p-2"
          data-modal-popover="true"
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="border-border mb-1 border-b px-2 pt-1 pb-2">
            <h3 className="text-sm font-semibold">Related matters</h3>
            <p className="text-muted-foreground mt-1 text-xs leading-5">
              View matters already connected to this matter.
            </p>
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {matters.map((matter) => (
              <li key={matter.id}>
                <Link
                  to="/matters/$matterId"
                  params={{ matterId: matter.id }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-muted focus-visible:bg-muted block rounded px-2 py-2 text-sm break-words text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {matter.title?.trim() || "Untitled matter"}
                </Link>
              </li>
            ))}
          </ul>
        </HoverCardContent>
      </Portal>
    </HoverCard>
  );
};
